<template>
    <div class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-content">
            <div class="modal-header">
                <div class="header-content">
                    <div class="icon-wrapper">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                        </svg>
                    </div>
                    <div>
                        <h2>Add New Guest</h2>
                        <p>Create a new guest entry with QR code generation</p>
                    </div>
                </div>
                <button class="close-btn" @click="$emit('close')" aria-label="Close modal">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>
            
            <div class="form-container">
                <div v-if="!qrCodeDataUrl" class="form-section">
                    <form @submit.prevent="submitForm">
                        <div v-if="isHostelAdmin" class="form-grid">
                            <div class="input-group">
                                <label for="roomNumber">Room Number <span class="required">*</span></label>
                                <div class="input-wrapper">
                                    <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" fill="currentColor"/>
                                    </svg>
                                    <select
                                        id="roomNumber"
                                        v-model="form.roomNumber"
                                        class="room-select"
                                        required
                                        :disabled="isSubmitting || loadingRooms"
                                    >
                                        <option value="" disabled>
                                            {{ loadingRooms ? 'Loading rooms...' : (availableRooms.length === 0 ? 'No rooms available' : 'Select room') }}
                                        </option>
                                        <option
                                            v-for="room in availableRooms"
                                            :key="room.objectspaceId"
                                            :value="room.roomno"
                                        >
                                            {{ room.roomno }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            
                            <div class="input-group">
                                <label for="mobileno">Mobile Number <span class="required">*</span></label>
                                <div class="input-wrapper">
                                    <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
                                    </svg>
                                    <input 
                                        type="tel" 
                                        id="mobileno" 
                                        v-model="form.mobileno" 
                                        required
                                        placeholder="Enter 10-digit mobile number"
                                        pattern="[0-9]{10}"
                                        maxlength="10"
                                        :disabled="isSubmitting"
                                    />
                                </div>
                            </div>

                            <div class="input-group">
                                <label for="onboardType">Onboard type <span class="required">*</span></label>
                                <div class="input-wrapper">
                                    <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                                    </svg>
                                    <select
                                        id="onboardType"
                                        v-model="form.onboardType"
                                        class="room-select"
                                        required
                                        :disabled="isSubmitting"
                                    >
                                        <option value="NEW">NEW</option>
                                        <option value="OLD">OLD</option>
                                    </select>
                                </div>
                            </div>

                            <div v-if="form.onboardType === 'OLD'" class="input-group">
                                <label for="onboardedon">Onboarded date <span class="required">*</span></label>
                                <p class="field-hint">Format: YYYY-MM-DD (e.g. 2026-02-16)</p>
                                <input
                                    id="onboardedon"
                                    v-model="form.onboardedon"
                                    type="date"
                                    class="input-plain"
                                    :required="form.onboardType === 'OLD'"
                                    :disabled="isSubmitting"
                                />
                            </div>
                        </div>
                        
                        <div class="form-actions">
                            <button type="button" class="btn-secondary" @click="$emit('close')" :disabled="isSubmitting">
                                Cancel
                            </button>
                            <button type="submit" class="btn-primary" :disabled="isSubmitting">
                                <span v-if="!isSubmitting">Add Guest</span>
                                <span v-else class="loading-content">
                                    <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" opacity="0.25"/>
                                        <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"/>
                                    </svg>
                                    Adding...
                                </span>
                            </button>
                        </div>
                    </form>
                </div>

                <!-- QR Code Success Display -->
                <div v-if="qrCodeDataUrl" class="qr-success-section">
                    <div class="success-header">
                        <div class="success-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" fill="#10B981"/>
                                <path d="M9 12l2 2 4-4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <h3>Guest Added Successfully!</h3>
                        <p>Share this QR code with the guest to complete registration</p>
                    </div>
                    
                    <div class="qr-display">
                        <div class="qr-container">
                            <img :src="qrCodeDataUrl" alt="Guest Registration QR Code" />
                        </div>
                        <div class="qr-actions">
                            <button type="button" class="btn-secondary" @click="downloadQR">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                Download QR
                            </button>
                            <button type="button" class="btn-primary" @click="resetForm">
                                Add Another Guest
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
        <transition-group name="toast" tag="div">
            <div 
                v-for="toast in toasts" 
                :key="toast.id" 
                class="toast"
                :class="[`toast-${toast.type}`]"
            >
                <div class="toast-icon">
                    <svg v-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <svg v-else-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22,4 12,14.01 9,11.01"/>
                    </svg>
                    <svg v-else-if="toast.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                    <svg v-else-if="toast.type === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                </div>
                <div class="toast-content">
                    <p class="toast-message">{{ toast.message }}</p>
                </div>
                <button class="toast-close" @click="removeToast(toast.id)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>
        </transition-group>
    </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import { readSessionToken } from '@/utilities/sessionTokenStorage';
import QRCode from 'qrcode';

export default {
    name: 'AddGuestForm',
    props: {
        objectSpaces: {
            type: Array,
            required: true,
        },
        objectData: {
            type: Object,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            isSubmitting: false,
            isHostelAdmin: false,
            isHotelAdmin: false,
            isHouseAdmin: false,
            isPropertyAdmin: false,
            objectSpaceProps: {},
            adminProfileData: {},
            adminType: '',
            form: {
                objectId: this.$route.params.id,
                objectType: '',
                roomNumber: '',
                mobileno: '',
                onboardType: 'NEW',
                onboardedon: '',
            },
            qrCodeDataUrl: null, // store QR image here
            toasts: [],
            availableRooms: [],
            loadingRooms: false,
        };
    },
    mounted() {
        let adminProfile = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = JSON.parse(adminProfile);
        if (this.objectSpaces && this.objectSpaces.length > 0) {
            this.objectSpaceProps = this.objectSpaces[0];
        }
        if (adminProfile) {
            try {
                adminProfile = JSON.parse(adminProfile);
                this.adminType = adminProfile.adminType;
                this.form.objectType = adminProfile.adminType;

                if (adminProfile.adminType === 'HOSTEL') {
                    this.isHostelAdmin = true;
                    this.fetchAvailableRooms();
                } else if (adminProfile.adminType === 'HOTEL') this.isHotelAdmin = true;
                else if (adminProfile.adminType === 'HOUSE') this.isHouseAdmin = true;
                else if (adminProfile.adminType === 'PROPERTY') this.isPropertyAdmin = true;
            } catch (e) {
                console.error('Error parsing admin profile data:', e);
            }
        }
    },
    methods: {
        // Toast notification methods
        showToast(message, type = 'error', duration = 5000) {
            const id = Date.now() + Math.random(); // Ensure unique ID
            this.toasts.push({ id, message, type });
            
            if (duration > 0) {
                setTimeout(() => {
                    this.removeToast(id);
                }, duration);
            }
        },
        removeToast(id) {
            this.toasts = this.toasts.filter(toast => toast.id !== id);
        },

        async fetchAvailableRooms() {
            const objectId = this.form.objectId || this.$route.params.id;
            if (!objectId) return;
            this.loadingRooms = true;
            this.availableRooms = [];
            try {
                const response = await apiService.getAvailableRoomNumbers(objectId);
                this.availableRooms = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                if (isNoDataError(error)) {
                    this.availableRooms = [];
                } else if (isHmsSubscriptionInactiveError(error)) {
                    // Subscription inactive — modal shown in App.vue
                } else {
                    this.handleApiError(error, 'Loading available rooms');
                }
            } finally {
                this.loadingRooms = false;
            }
        },

        // Enhanced API error handling
        handleApiError(error, context = 'Operation') {
            // Skip error handling for "no data" cases (status 400 with errorcode 2003)
            if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
                return;
            }

            console.error(`${context} error:`, error);
            
            let message = null;
            let type = 'error';

            if (error?.response?.data) {
                const errorData = error.response.data;
                const status = error.response.status;
                
                // For HTTP 400 errors, always use the reason field from API response
                if (status === 400) {
                    if (errorData.reason) {
                        message = errorData.reason;
                    }
                    // If no reason provided for 400 error, don't show toast (no generic messages)
                } else {
                    // For other status codes, prioritize reason field
                    if (errorData.reason) {
                        message = errorData.reason;
                    } else if (errorData.message) {
                        message = errorData.message;
                    } else {
                        // Fallback messages for other status codes only if no reason/message
                        switch (status) {
                            case 401:
                                message = 'Session expired. Please login again.';
                                setTimeout(() => {
                                    this.$router.push('/login');
                                }, 2000);
                                break;
                            case 403:
                                message = 'You do not have permission to perform this action.';
                                break;
                            case 404:
                                message = 'The requested resource was not found.';
                                break;
                            case 429:
                                message = 'Too many requests. Please wait a moment and try again.';
                                break;
                            case 500:
                                message = 'Server error occurred. Please try again later.';
                                break;
                            case 503:
                                message = 'Service temporarily unavailable. Please try again later.';
                                break;
                        }
                    }
                }
            } else if (error?.request) {
                message = 'No response from server. Please check your internet connection.';
            } else if (error?.message) {
                message = error.message;
            }

            // Only show toast if we have a message (genuine errors only)
            if (message) {
                this.showToast(message, type);
            }
        },

        // Helper method to get authentication token
        getAuthToken() {
            try {
                const token = readSessionToken();
                if (!token) {
                    throw new Error('No authentication token found');
                }
                return token;
            } catch (error) {
                console.error('Error getting auth token:', error);
                this.showToast('Authentication error. Please login again.', 'error');
                setTimeout(() => {
                    this.$router.push('/login');
                }, 2000);
                throw error;
            }
        },

        async submitForm() {
            // Validate form before submission
            if (!this.form.roomNumber.trim()) {
                this.showToast('Please enter a room number.', 'warning');
                return;
            }
            if (!this.form.mobileno.trim() || this.form.mobileno.length !== 10) {
                this.showToast('Please enter a valid 10-digit mobile number.', 'warning');
                return;
            }

            if (this.form.onboardType === 'OLD') {
                const onboardedon = String(this.form.onboardedon || '').trim();
                if (!/^\d{4}-\d{2}-\d{2}$/.test(onboardedon)) {
                    this.showToast('Please select an onboarded date (YYYY-MM-DD).', 'warning');
                    return;
                }
            }

            await this.AddGuest();
        },

        async AddGuest() {
            try {
                this.isSubmitting = true;
                const payload = new URLSearchParams();
                payload.append('roomNumber', this.form.roomNumber);
                payload.append('mobileno', this.form.mobileno);
                payload.append('objectId', this.form.objectId);
                payload.append('objectType', this.form.objectType);
                payload.append('onboardType', this.form.onboardType === 'OLD' ? 'OLD' : 'NEW');
                if (this.form.onboardType === 'OLD' && String(this.form.onboardedon || '').trim()) {
                    payload.append('onboardedon', String(this.form.onboardedon).trim());
                }

                const response = await apiService.addGuest(payload);
                
                if (!response.data) {
                    throw new Error('Invalid response from server');
                }

                const responseToken = response.data.success;

                if (responseToken) {
                    this.showToast('Guest added successfully!', 'success', 3000);
                    await this.generateQRCode(responseToken);
                    // Emit event to notify parent component to refresh the guest list
                    this.$emit('guest-added');
                } else {
                    throw new Error('No token received from server');
                }
            } catch (error) {
                this.handleApiError(error, 'Adding guest');
            } finally {
                this.isSubmitting = false;
            }
        },

        async generateQRCode(text) {
            try {
                if (!text) {
                    throw new Error('No data provided for QR code generation');
                }

                const qrDataUrl = await QRCode.toDataURL(text, {
                    width: 256,
                    margin: 2,
                    color: {
                        dark: '#5b2e8e',
                        light: '#ffffff'
                    }
                });
                this.qrCodeDataUrl = qrDataUrl;
            } catch (err) {
                console.error('Failed to generate QR code', err);
                this.showToast('Failed to generate QR code. Please try again.', 'error');
            }
        },

        downloadQR() {
            try {
                if (!this.qrCodeDataUrl) {
                    this.showToast('No QR code available to download.', 'warning');
                    return;
                }

                const link = document.createElement('a');
                link.download = `guest-qr-${this.form.roomNumber || 'code'}.png`;
                link.href = this.qrCodeDataUrl;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.showToast('QR code downloaded successfully!', 'success', 3000);
            } catch (error) {
                console.error('Error downloading QR code:', error);
                this.showToast('Failed to download QR code. Please try again.', 'error');
            }
        },

        resetForm() {
            this.form.roomNumber = '';
            this.form.mobileno = '';
            this.form.onboardType = 'NEW';
            this.form.onboardedon = '';
            this.qrCodeDataUrl = null;
            this.fetchAvailableRooms();
        },

        validateMobileNumber() {
            const mobile = this.form.mobileno.replace(/\D/g, '');
            this.form.mobileno = mobile.slice(0, 10);
        }
    }
};
</script>




<style scoped>
/* Modal Overlay */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

/* Modal Content */
.modal-content {
    background: #ffffff;
    border-radius: 16px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    animation: slideUp 0.3s ease-out;
    position: relative;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Modal Header */
.modal-header {
    background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
    color: white;
    padding: 24px 32px;
    position: relative;
    overflow: hidden;
}

.modal-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
    pointer-events: none;
}

.header-content {
    display: flex;
    align-items: center;
    gap: 16px;
    position: relative;
    z-index: 1;
}

.header-content > div {
    text-align: left;
}

.icon-wrapper {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-header h2 {
    margin: 0 0 4px 0;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    text-align: left;
}

.modal-header p {
    margin: 0;
    font-size: 14px;
    opacity: 0.9;
    font-weight: 400;
    text-align: left;
}

.close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
    z-index: 2;
}

.close-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

/* Form Container */
.form-container {
    padding: 32px;
    overflow-y: auto;
    max-height: calc(90vh - 120px);
}

.form-section {
    animation: slideIn 0.3s ease-out 0.1s both;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Form Grid */
.form-grid {
    display: grid;
    gap: 24px;
    margin-bottom: 32px;
}

.input-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Labels */
label {
    font-weight: 600;
    font-size: 14px;
    color: #374151;
    line-height: 1.4;
    text-align: left;
    display: block;
}

.required {
    color: #ef4444;
    font-size: 12px;
    margin-left: 2px;
}

.field-hint {
    margin: 0 0 4px 0;
    font-size: 12px;
    color: #6b7280;
    font-weight: 400;
}

.input-plain {
    width: 100%;
    padding: 14px 16px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 16px;
    background: #ffffff;
    transition: all 0.2s ease;
    box-sizing: border-box;
}

.input-plain:focus {
    outline: none;
    border-color: #5b2e8e;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.input-plain:disabled {
    background: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
}

/* Input Wrapper */
.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 12px;
    color: #6b7280;
    z-index: 1;
    pointer-events: none;
}

/* Room select - matches input styling */
.room-select {
    width: 100%;
    padding: 14px 16px 14px 44px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 16px;
    background: #ffffff;
    transition: all 0.2s ease;
    box-sizing: border-box;
    appearance: auto;
    cursor: pointer;
}

.room-select:focus {
    outline: none;
    border-color: #5b2e8e;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.room-select:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
    color: #9ca3af;
}

/* Input Styles */
input {
    width: 100%;
    padding: 14px 16px 14px 44px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 16px;
    background: #ffffff;
    transition: all 0.2s ease;
    box-sizing: border-box;
}

input:focus {
    outline: none;
    border-color: #5b2e8e;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

input:disabled {
    background: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
}

input::placeholder {
    color: #9ca3af;
    font-size: 14px;
}

/* Button Styles */
.btn-primary {
    background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
    color: white;
    border: none;
    border-radius: 12px;
    padding: 14px 28px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 25px -5px rgba(91, 46, 142, 0.4);
}

.btn-primary:active:not(:disabled) {
    transform: translateY(0);
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.btn-secondary {
    background: #ffffff;
    color: #6b7280;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px 28px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
}

.btn-secondary:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateY(-1px);
}

.btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

/* Form Actions */
.form-actions {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    margin-top: 32px;
}

/* Loading Animation */
.loading-content {
    display: flex;
    align-items: center;
    gap: 8px;
}

.spinner {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* QR Success Section */
.qr-success-section {
    text-align: center;
    animation: slideIn 0.4s ease-out;
}

.success-header {
    margin-bottom: 32px;
}

.success-icon {
    margin-bottom: 16px;
    display: flex;
    justify-content: center;
}

.success-header h3 {
    margin: 0 0 8px 0;
    font-size: 24px;
    font-weight: 700;
    color: #10b981;
}

.success-header p {
    margin: 0;
    font-size: 16px;
    color: #6b7280;
}

/* QR Display */
.qr-display {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
}

.qr-container {
    background: #ffffff;
    border: 2px solid #e5e7eb;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.qr-container img {
    display: block;
    width: 200px;
    height: 200px;
    border-radius: 8px;
}

.qr-actions {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    justify-content: center;
}

/* Responsive Design */
@media (max-width: 768px) {
    .modal-content {
        margin: 16px;
        width: calc(100% - 32px);
        max-height: calc(100vh - 32px);
    }

    .modal-header {
        padding: 20px 24px;
    }

    .form-container {
        padding: 24px;
    }

    .header-content {
        gap: 12px;
    }

    .modal-header h2 {
        font-size: 20px;
    }

    .form-actions {
        flex-direction: column;
    }

    .btn-primary,
    .btn-secondary {
        width: 100%;
    }

    .qr-actions {
        flex-direction: column;
        width: 100%;
    }

    .qr-actions .btn-primary,
    .qr-actions .btn-secondary {
        width: 100%;
    }
}

@media (max-width: 480px) {
    .modal-header {
        padding: 16px 20px;
    }

    .form-container {
        padding: 20px;
    }

    .qr-container {
        padding: 16px;
    }

    .qr-container img {
        width: 160px;
        height: 160px;
    }
}

/* Toast Notification Styles */
.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 400px;
    pointer-events: none;
}

.toast {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    border-left: 4px solid transparent;
    min-width: 320px;
    pointer-events: auto;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.toast-error {
    border-left-color: #ef4444;
    background: linear-gradient(135deg, #fef2f2, #ffffff);
}

.toast-success {
    border-left-color: #10b981;
    background: linear-gradient(135deg, #f0fdf4, #ffffff);
}

.toast-info {
    border-left-color: #3b82f6;
    background: linear-gradient(135deg, #eff6ff, #ffffff);
}

.toast-warning {
    border-left-color: #f59e0b;
    background: linear-gradient(135deg, #fffbeb, #ffffff);
}

.toast-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
}

.toast-error .toast-icon {
    color: #ef4444;
}

.toast-success .toast-icon {
    color: #10b981;
}

.toast-info .toast-icon {
    color: #3b82f6;
}

.toast-warning .toast-icon {
    color: #f59e0b;
}

.toast-content {
    flex: 1;
    min-width: 0;
}

.toast-message {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    line-height: 1.4;
    word-wrap: break-word;
}

.toast-close {
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.toast-close:hover {
    color: #6b7280;
    background: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
}

/* Toast Animations */
.toast-enter-active {
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-move {
    transition: transform 0.3s ease;
}

/* Responsive Toast Styles */
@media (max-width: 768px) {
    .toast-container {
        top: 16px;
        right: 16px;
        left: 16px;
        max-width: none;
    }

    .toast {
        min-width: auto;
        width: 100%;
        padding: 14px 16px;
    }

    .toast-message {
        font-size: 14px;
    }
}

/* Focus and accessibility improvements */
.btn-primary:focus,
.btn-secondary:focus,
.close-btn:focus {
    outline: 2px solid #5b2e8e;
    outline-offset: 2px;
}

/* Smooth transitions for better UX */
* {
    box-sizing: border-box;
}

input,
button {
    font-family: inherit;
}
</style>